import { Filter } from "@/types/Filter";
import { ConditionGroup, ConditionFilter } from "@/types/EventRule";
import { Field } from "@/types/Field";
import {
  bareFieldName,
  canonicalFieldKey,
  findFieldByKey,
} from "@/components/ConditionBuilder/condition-utils";
import { SYSTEM_FILTER_FIELDS, parseFiltersApiRequest } from "@/lib/utils/parser";

/**
 * Converters between the legacy flat guest-list filter model (glyph operators,
 * implicit AND across keys / OR within a key — see backend FilterParser) and
 * the nested ConditionGroup word-token model. The FE mapping MUST stay in
 * lockstep with backend `FlatFilterToConditionsConverter` — both are pinned by
 * tests asserting the same mapping table.
 */

/** Legacy glyph → ConditionOperator word token. */
const GLYPH_TO_WORD: Record<string, string> = {
  "=": "=",
  "≠": "!=",
  "~": "contains",
  "∅": "is_empty",
  "⊙": "is_not_empty",
  ">": ">",
  "≥": ">=",
  "<": "<",
  "≤": "<=",
};

/** Word token → legacy glyph (only for flat-representable operators). */
const WORD_TO_GLYPH: Record<string, string> = Object.fromEntries(
  Object.entries(GLYPH_TO_WORD).map(([g, w]) => [w, g]),
);

const VALUELESS_WORDS = new Set(["is_empty", "is_not_empty"]);

/** The legacy glyph for a word operator, or `null` when it has none (the flat
 *  model cannot say `not_contains`, `starts_with`, …). Exported so the inline
 *  filter row projects a tree with the SAME mapping table this file's
 *  converters use — a second copy is a second thing to drift. */
export function wordToGlyph(word: string): string | null {
  return WORD_TO_GLYPH[word] ?? null;
}

/** Does this word operator carry no value (`is_empty` / `is_not_empty`)? */
export function isValuelessWord(word: string): boolean {
  return VALUELESS_WORDS.has(word);
}

const scalar = (v: Filter["value"]): string =>
  Array.isArray(v) ? (v[0] ?? "") : v;

/**
 * Flat glyph filters → nested ConditionGroup, preserving the backend's flat
 * semantics (OR within same key, AND across keys):
 *  - same-key groups where every row is `=` → one `in` filter (value array),
 *  - same-key groups where every row is `≠` → one `not_in` filter,
 *  - other same-key multi-row groups → an OR subgroup,
 *  - singleton keys → plain filters on the root AND group.
 * Insertion order of first key occurrence is preserved. Unknown glyphs throw —
 * the caller decides whether to fall back to the legacy path.
 */
export function flatFiltersToConditionGroup(filters: Filter[]): ConditionGroup {
  const byKey = new Map<string, Filter[]>();
  for (const f of filters ?? []) {
    if (!f?.key) continue;
    const list = byKey.get(f.key) ?? [];
    list.push(f);
    byKey.set(f.key, list);
  }

  const rootFilters: ConditionFilter[] = [];
  const rootGroups: ConditionGroup[] = [];

  for (const [key, rows] of byKey) {
    const words = rows.map((r) => {
      const w = GLYPH_TO_WORD[r.operator];
      if (!w) throw new Error(`Unknown flat operator: ${r.operator}`);
      return w;
    });

    if (rows.length === 1) {
      rootFilters.push({
        key,
        operator: words[0],
        value: VALUELESS_WORDS.has(words[0]) ? "" : scalar(rows[0].value),
      });
      continue;
    }
    if (words.every((w) => w === "=")) {
      rootFilters.push({ key, operator: "in", value: rows.map((r) => scalar(r.value)) });
      continue;
    }
    if (words.every((w) => w === "!=")) {
      rootFilters.push({ key, operator: "not_in", value: rows.map((r) => scalar(r.value)) });
      continue;
    }
    // Mixed operators on one key: keep the backend's same-key OR as an explicit
    // OR subgroup.
    rootGroups.push({
      operator: "OR",
      filters: rows.map((r, i) => ({
        key,
        operator: words[i],
        value: VALUELESS_WORDS.has(words[i]) ? "" : scalar(r.value),
      })),
      groups: [],
    });
  }

  return { operator: "AND", filters: rootFilters, groups: rootGroups };
}

/**
 * Nested ConditionGroup → flat glyph filters, or `null` when the tree is not
 * flat-representable. Flat-representable means: root is AND; leaf operators
 * map to glyphs (`in`/`not_in` expand to per-value `=`/`≠` rows); nested
 * groups only in the exact same-key-OR shape the forward converter emits.
 * Used for the saved-filter dual-write so legacy consumers (CSV export, bulk
 * payloads, older app versions) keep working on migrated filters.
 */
export function conditionGroupToFlatFilters(
  group: ConditionGroup | undefined,
): Filter[] | null {
  if (!group) return [];
  if (group.operator !== "AND") return null;

  const flat: Filter[] = [];

  for (const f of group.filters ?? []) {
    if (!f.key) continue;
    if (f.operator === "in" || f.operator === "not_in") {
      const glyph = f.operator === "in" ? "=" : "≠";
      const values = Array.isArray(f.value) ? f.value : [f.value];
      for (const v of values) flat.push({ key: f.key, operator: glyph, value: String(v ?? "") });
      continue;
    }
    const glyph = WORD_TO_GLYPH[f.operator];
    if (!glyph) return null; // contains-variants map, not_contains etc. don't
    flat.push({
      key: f.key,
      operator: glyph,
      value: VALUELESS_WORDS.has(f.operator) ? "" : String(scalar(f.value as Filter["value"]) ?? ""),
    });
  }

  for (const sub of group.groups ?? []) {
    // Only the same-key OR shape survives flattening (backend ORs same-key rows).
    if (sub.operator !== "OR" || (sub.groups?.length ?? 0) > 0) return null;
    const rows = sub.filters ?? [];
    if (rows.length === 0) continue;
    const key = rows[0].key;
    if (!rows.every((r) => r.key === key)) return null;
    for (const r of rows) {
      const glyph = WORD_TO_GLYPH[r.operator];
      if (!glyph) return null;
      flat.push({
        key,
        operator: glyph,
        value: VALUELESS_WORDS.has(r.operator) ? "" : String(scalar(r.value as Filter["value"]) ?? ""),
      });
    }
  }

  return flat;
}

/** Bare keys the backend reads TRI-STATE (null = never scanned / unknown) —
 *  `FilterParser.TRI_STATE_BOOLEANS`. Its flat `= false` is `$in:[false,null]`
 *  and its flat `≠ false` is `$nin:[false,null]`; the tree lane compiles both
 *  as a plain `$eq`/`$ne`. Only the `true` operand means the same on both. */
const TRI_STATE_KEYS = new Set(["checkedIn", "emailUndeliverable"]);

/** Word operators whose single leaf compiles identically on both lanes (the
 *  per-leaf rules in `leafMatchesFlat` narrow some of them further). */
const FLAT_SAFE_WORDS = new Set([
  "=", "!=", "in", "not_in", "contains", "is_empty", "is_not_empty", ">", ">=", "<", "<=",
]);

/** The keys the backend's tree lane reads as BUILT-IN guest fields —
 *  `GuestDisplayConditionValidator.ALLOWED_KEYS`. Any other bare key is
 *  rewritten to `additionalFields.<key>` there (`GuestConditionKeyNormalizer`),
 *  while the flat lane reads the real field: `guestId`, `mainGuest`,
 *  `submittedAt` and friends mean one thing in the list and another in the
 *  action. Pinned against the backend set through the shared fixture's
 *  `treeBuiltInKeys` (both repos assert it). */
export const TREE_BUILT_IN_GUEST_KEYS: ReadonlySet<string> = new Set([
  "guestType", "firstName", "lastName", "gender", "title", "jobTitle",
  "company", "email", "phone", "mobile", "address", "address2",
  "zipCode", "city", "country", "language", "emailOptIn", "username",
  "smsOptIn",
  "code", "state", "acceptanceState", "checkedIn", "checkedInAt",
  "numCompanions", "createdAt", "updatedAt", "archived",
  "apptCount", "apptStatuses", "nextApptAt", "hasOpenRequest",
  "apptOfferingIds", "apptTreatmentIds", "apptLaneIds",
  "seatCount", "seatStatuses", "seatRoomIds",
  "lastEmailedAt", "lastEmailStatus", "lastEmailOpenedAt", "lastEmailClickedAt",
  "emailedTemplateIds", "emailedSendNames", "portalVisitedAt", "websiteLoginAt",
  "emailUndeliverable", "emailUndeliverableAt",
  "origin", "importId",
  "speakerId",
]);

/** The tree validator's caps (`GuestQueryConditionValidator`): more leaves
 *  (every entry counts, `archived` too), a longer `in` list, a longer operand
 *  or `contains` pattern, and the LIST request fails while the flat action
 *  would not — the list then keeps the previous filter's rows on screen. */
const TREE_MAX_LEAVES = 100;
const TREE_MAX_LIST_VALUES = 200;
const TREE_MAX_VALUE_LENGTH = 500;
const TREE_MAX_PATTERN_LENGTH = 256;

/** The built-in keys main-consumer coerces a number-like operand to a number
 *  on (`filter-parser.ts` `NUMERIC_KEYS`); custom fields are coerced too.
 *  The other built-ins keep the operand a string on all three lanes (booleans
 *  and ObjectIds aside, which read alike), so a PLZ `01067` or a phone prefix
 *  `0171` means the same everywhere. */
const CONSUMER_NUMERIC_KEYS = new Set(["numCompanions", "apptCount", "seatCount"]);

/** The built-in date keys (`filter-parser.ts` `DATE_KEYS`, Java
 *  `FilterParser.convertValueToAppropriateType`). Java makes the operand a date
 *  only by `Instant.parse` and keeps any other text; the consumer makes a date
 *  of whatever `new Date` reads (`2026-10-01`, a local `…T00:00`, even `123`).
 *  So the list compares text against a date (0 rows) while export and bulk
 *  e-mail act on real guests. Only an exact instant reads alike. */
const CONSUMER_DATE_KEYS = new Set([
  "updatedAt",
  "createdAt",
  "checkedInAt",
  "nextApptAt",
  "lastEmailedAt",
  "lastEmailOpenedAt",
  "lastEmailClickedAt",
  "portalVisitedAt",
  "websiteLoginAt",
  "submittedAt",
  "emailUndeliverableAt",
  "sentAt",
]);

/** Keys Java's flat lane renames before it queries (`GuestScopeQuery`
 *  `guestId` → `_id`) and main-consumer's user-filter lane does not: `≠ X` there
 *  matches every guest. */
const JAVA_RENAMED_KEYS = new Set(["guestId"]);

const EXACT_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;
/** Looks like a date-time to either parser — case-insensitive, as Java's is. */
const DATE_LIKE = /\d{4}-\d{2}-\d{2}[Tt]/;

/** An exact instant both parsers read as the same moment: the uppercase `…Z`
 *  form that `new Date` reads back unchanged. A leap second (`23:59:60`), a
 *  day that does not exist (`04-31`) or `24:00` — where Java refuses or clamps
 *  and JS rolls over — fails the round trip. */
function isExactInstant(v: string): boolean {
  if (!EXACT_INSTANT.test(v)) return false;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return false;
  const fraction = /\.(\d{1,3})Z$/.exec(v)?.[1] ?? "";
  return d.toISOString() === v.replace(/(\.\d{1,3})?Z$/, `.${fraction.padEnd(3, "0")}Z`);
}

/** Which coercion the consumer applies to this key's operand, if any. */
type CoercedKind = "custom" | "numeric" | "date";
function coercedKind(key: string, allFields: Field[]): CoercedKind | null {
  const custom = key.startsWith("additionalFields.") || findFieldByKey(allFields, key)?.additionalField === true;
  if (custom) return "custom";
  const bare = bareFieldName(key);
  if (CONSUMER_NUMERIC_KEYS.has(bare)) return "numeric";
  if (CONSUMER_DATE_KEYS.has(bare)) return "date";
  return null;
}

/** Does main-consumer — which compiles the flat rows of bulk e-mail and the
 *  export — read this operand as both Java lanes do? Only asked for the keys
 *  the consumer coerces. All three turn a plain decimal into a number (Java a
 *  `Long`, else a `Double`), but:
 *  - an integer past 2^53 is exact in Java and another number in JS;
 *  - `contains` turns the number back into text: `"2.0"` stays `\Q2.0\E` in
 *    Java and becomes the regex `2` in the consumer, `12345678.5` is
 *    `1.23456785E7` in Java — only a canonical integer reads alike;
 *  - on a numeric built-in the consumer parses any leading digits
 *    (`"12abc"` → 12) where Java keeps the text;
 *  - a custom date-like operand is a date to the consumer whenever `new Date`
 *    parses it (no zone = local time), to Java only as an exact instant —
 *    and `contains` on a date makes Java search its own spelling of it;
 *  - on a built-in date key, see `CONSUMER_DATE_KEYS`. */
function operandSpelledAlike(v: string, operator: string, kind: CoercedKind): boolean {
  if (kind === "date") return operator !== "contains" && isExactInstant(v);
  // Java decides "boolean or text" with `equalsIgnoreCase`, which folds the
  // long s (`falſe` = false); the consumer lower-cases, which does not.
  const upper = v.toUpperCase();
  if ((upper === "TRUE" || upper === "FALSE") && v.toLowerCase() !== upper.toLowerCase()) return false;
  const numericKey = kind === "numeric";
  if (/^-?\d+(\.\d+)?$/.test(v)) {
    const integer = /^-?\d+$/.test(v);
    if (integer && !Number.isSafeInteger(Number(v))) return false;
    if (operator === "contains") return integer && String(Number(v)) === v;
    return true;
  }
  if (numericKey) return false;
  if (DATE_LIKE.test(v)) return operator !== "contains" && isExactInstant(v);
  return true;
}

/**
 * The same question for flat rows the list sends as they are (no tree): Java
 * compiles the list, main-consumer the export and bulk e-mail from the very
 * same rows — does every operand read alike on both? Only the coerced keys
 * are asked; an empty-test row carries no operand.
 */
export function flatRowsReadAlike(rows: Filter[], allFields: Field[]): boolean {
  return rows.every((r) => {
    if (!r || typeof r.key !== "string") return false;
    if (JAVA_RENAMED_KEYS.has(bareFieldName(r.key))) return false;
    const kind = coercedKind(r.key, allFields);
    if (!kind) return true;
    const word = GLYPH_TO_WORD[r.operator];
    if (!word) return false;
    if (VALUELESS_WORDS.has(word)) return true;
    const values = Array.isArray(r.value) ? r.value : [r.value];
    return values.every((v) => operandSpelledAlike(String(v ?? ""), word, kind));
  });
}

function leafMatchesFlat(leaf: ConditionFilter, allFields: Field[]): boolean {
  if (!leaf.key || leaf.key === "#expr" || leaf.expr !== undefined) return false;
  if (!FLAT_SAFE_WORDS.has(leaf.operator)) return false;
  const bare = bareFieldName(leaf.key);
  // A dotted path that is not a custom field (`seatInfo.roomId`): the flat
  // lane refuses it and the tree lane does not.
  if (bare.includes(".")) return false;
  const multi = leaf.operator === "in" || leaf.operator === "not_in";
  if (multi !== Array.isArray(leaf.value)) return false;
  const valueless = leaf.operator === "is_empty" || leaf.operator === "is_not_empty";
  // The tree validator caps the value of EVERY leaf, an emptiness test's too.
  if (valueless && typeof leaf.value === "string" && leaf.value.length > TREE_MAX_VALUE_LENGTH) return false;
  const operands = valueless ? [] : multi ? (leaf.value as unknown[]) : [leaf.value];
  if (operands.length > TREE_MAX_LIST_VALUES) return false;
  // The tree compares a `null` or a number as it is; the flat row sends its
  // text (`= null` → `= ""`), a different question.
  if (!operands.every((v): v is string => typeof v === "string")) return false;
  if (operands.some((v) => (v as string).length > TREE_MAX_VALUE_LENGTH)) return false;
  if (leaf.operator === "contains" && (leaf.value as string).length > TREE_MAX_PATTERN_LENGTH) return false;
  if (TRI_STATE_KEYS.has(bare)) {
    return (leaf.operator === "=" || leaf.operator === "!=") && leaf.value === "true";
  }
  const kind = coercedKind(leaf.key, allFields);
  const custom = kind === "custom";
  if (!custom && !TREE_BUILT_IN_GUEST_KEYS.has(bare)) return false;
  if (kind && !operands.every((v) => operandSpelledAlike(v as string, leaf.operator, kind))) {
    return false;
  }
  // Tree `!=` on a custom field adds `$exists:true`; flat `≠` is a plain `$ne`
  // and also hits every guest without a value.
  if (leaf.operator === "!=" && custom) return false;
  // `in []` derives no flat row at all (the key vanishes ⇒ wider); `not_in`
  // with 2+ values derives `≠a`, `≠b`, which the flat lane ORs ⇒ everyone.
  if (leaf.operator === "in" && (leaf.value as string[]).length === 0) return false;
  if (leaf.operator === "not_in" && (leaf.value as string[]).length !== 1) return false;
  return true;
}

/** A root `archived` leaf the list request can carry: `=` / `!=` on a
 *  boolean, the only shape the editor and the flat converter write. */
function archivedLeafValid(f: ConditionFilter): boolean {
  if (f.operator !== "=" && f.operator !== "!=") return false;
  return typeof f.value === "boolean" || (typeof f.value === "string" && /^(true|false)$/i.test(f.value));
}

function allLeavesKeyed(group: ConditionGroup): boolean {
  if (!(group.filters ?? []).every((f) => !!f && typeof f.key === "string" && f.key.trim() !== "")) {
    return false;
  }
  return (group.groups ?? []).every((g) => !!g && allLeavesKeyed(g));
}

function countLeaves(group: ConditionGroup): number {
  return (group.groups ?? []).reduce((n, g) => n + countLeaves(g), group.filters?.length ?? 0);
}

/**
 * Does an "all matching" bulk action — which SENDS the flat rows derived from
 * this tree — act on exactly the guests the list SHOWS by the tree?
 *
 * The two lanes are compiled by different backend builders (FilterParser vs
 * ConditionGroupQueryBuilder) that disagree in places: the flat lane ORs every
 * row on one key (a range, `≠ A, B`, a key with two sources), keeps no
 * `$exists` on a custom-field `≠`, and reads `checkedIn = false` tri-state.
 * Where they disagree, a filter showing 3 guests sent SMS, mail, edits or
 * deletions to the whole list. This answers true only for shapes proven to
 * compile to the same query — the shared fixture
 * `__tests__/fixtures/flat-conditions-parity.json` is asserted here AND by
 * main-backend `FlatVsConditionsParityTest` against the real builders.
 *
 * `sentFlat` is the flat list the caller will actually send (the dual-written
 * `filter`); when given, it must be exactly this tree's derivation, so a saved
 * filter whose two halves drifted apart is refused too.
 */
export function flatLaneMatchesTree(
  group: ConditionGroup | undefined,
  allFields: Field[],
  sentFlat?: Filter[],
): boolean {
  if (!group || typeof group !== "object" || Array.isArray(group)) return true;
  if (!(group.filters?.length ?? 0) && !(group.groups?.length ?? 0)) return true;
  if (group.operator !== "AND") return false;
  // Every leaf, at every level, needs a real key — the tree validator answers
  // a blank one with 400 (`ConditionGroupValidator.allKeysValid`), and the
  // list then keeps the previous filter's rows while the derivation skips
  // the row and sends a wider question. Its leaf cap counts every entry.
  if (!allLeavesKeyed(group)) return false;
  if (countLeaves(group) > TREE_MAX_LEAVES) return false;

  const derived = conditionGroupToFlatFilters(group);
  if (derived === null) return false;

  // One source per field: two root leaves, or a root leaf beside a same-key
  // OR group, are ANDed in the tree and ORed by the flat lane. Bare and
  // `additionalFields.`-qualified spellings are one field on the backend.
  const sources = new Map<string, number>();
  const leaves: ConditionFilter[] = [];
  const addSource = (key: string) => {
    const bare = bareFieldName(key);
    sources.set(bare, (sources.get(bare) ?? 0) + 1);
  };
  for (const f of group.filters ?? []) {
    // The bulk payload sends `archived` on its own — but the tree validator
    // still checks the leaf, and a leaf it refuses keeps the old list on screen.
    if (f.key === "archived") {
      if (!archivedLeafValid(f)) return false;
      continue;
    }
    addSource(f.key);
    leaves.push(f);
  }
  for (const sub of group.groups ?? []) {
    const rows = sub.filters ?? [];
    if (rows.length === 0) continue;
    addSource(rows[0].key);
    leaves.push(...rows);
  }
  for (const n of sources.values()) if (n !== 1) return false;
  if (!leaves.every((leaf) => leafMatchesFlat(leaf, allFields))) return false;

  // The parser must pass every row through unchanged apart from the custom
  // key prefix: a dropped row (unresolved key) widens, a coerced operator
  // (`= ""` → `∅`) or a rewritten operand is no longer the tree's question.
  const rows = derived.filter((r) => r.key !== "archived");
  const parsed = parseFiltersApiRequest(rows.map((r) => ({ ...r })), allFields);
  if (parsed.length !== rows.length) return false;
  const same = (a: Filter, b: Filter) =>
    bareFieldName(a.key) === bareFieldName(b.key) &&
    a.operator === b.operator &&
    a.value === b.value;
  if (!parsed.every((p, i) => same(p, rows[i]))) return false;

  if (sentFlat && !sameFlatRows(sentFlat, rows, allFields)) return false;
  return true;
}

/**
 * Are two flat row lists the same question on the flat lane? Order-free (a
 * tree the backend backfilled from a legacy flat filter regroups its rows by
 * key, which is the same question in another order), `archived` ignored (the
 * bulk payload sends it on its own), and compared on the keys as the parser
 * SENDS them — a bare legacy `tier` and `additionalFields.tier` are one key,
 * a built-in spelled `additionalFields.company` is not `company`. A keyless
 * row is never the same.
 */
export function sameFlatRows(a: Filter[], b: Filter[], allFields: Field[]): boolean {
  const norm = (rows: Filter[]): string[] | null => {
    const kept = rows.filter((r) => r?.key !== "archived");
    if (!kept.every((r) => typeof r?.key === "string" && r.key !== "")) return null;
    return parseFiltersApiRequest(kept.map((r) => ({ ...r })), allFields, { onUnresolvedKey: "keep" })
      .map((r) => JSON.stringify([r.key, r.operator, r.value]))
      .sort();
  };
  const x = norm(a);
  const y = norm(b);
  if (!x || !y || x.length !== y.length) return false;
  return x.every((s, i) => s === y[i]);
}

/**
 * AND-merge system-injected flat filters (duplicate-mode etc.) into a
 * conditions tree for the request body. NOT for `archived` — that travels as a
 * request-level boolean in conditions mode.
 */
export function mergeFlatIntoGroup(
  group: ConditionGroup,
  flatFilters: Filter[],
): ConditionGroup {
  if (!flatFilters?.length) return group;
  const merged = flatFiltersToConditionGroup(flatFilters);
  return {
    operator: "AND",
    filters: [
      ...(group.operator === "AND" ? group.filters ?? [] : []),
      ...(merged.filters ?? []),
    ],
    groups: [
      ...(group.operator === "AND"
        ? group.groups ?? []
        : [group]), // non-AND user tree nests intact under the AND root
      ...(merged.groups ?? []),
    ],
  };
}

/**
 * Merge dashboard/goals click-through URL filters into a saved conditions
 * tree, mirroring the flat-model semantics ("a URL filter on a key REPLACES
 * any saved row on that same key"), with two deliberate refinements:
 *
 * - Rows whose key resolves to no field (and is not a system filter key) are
 *   DROPPED, exactly like `parseFiltersApiRequest` drops them on the flat
 *   path — otherwise a stray query param (`?utm_source=…`, a deleted field in
 *   an old bookmark) becomes a conditions leaf the backend normalizes into a
 *   nonexistent `additionalFields.*` key and the list silently matches zero.
 * - Replacement is AND-context only. Leaves inside OR groups are disjuncts:
 *   removing one changes what its SIBLINGS mean (`OR(checkedIn, vip)` minus
 *   `checkedIn` AND-merged with `checkedIn=true` would exclude checked-in
 *   non-VIPs the user's click clearly targets). A mixed-key OR group is kept
 *   intact — AND-merging the URL row already narrows it to exactly the
 *   clicked segment. Only an OR group whose leaves ALL sit on URL keys (the
 *   same-key OR shape the flat converter emits) is dropped wholesale: keeping
 *   it would AND contradictory values (`kategorie=VIP ∧ kategorie=Presse` ⇒
 *   empty list).
 *
 * URL rows arrive with BARE custom-field keys (the flat-path convention —
 * `parseFiltersApiRequest` requires bare); the tree stores DOTTED keys. The
 * same-field comparison is therefore bare/dotted-agnostic (`bareFieldName` —
 * names are collision-free by field-creation guards), and the emitted leaves
 * are canonicalized to the dotted wire key, so the resulting tree stays
 * uniformly dotted — exact drawer display, and the TableFilter save validator
 * (which rejects bare custom keys) stays satisfied. Empty-equals URL rows are
 * coerced to the emptiness operators BEFORE conversion (`("=","")` → ∅,
 * `("≠","")` → ⊙ — flat-path parity; the dashboard "never scanned" segment
 * links with an empty value and the conditions path has no server-side
 * coercion). `flatFiltersToConditionGroup` itself is pinned in lockstep with
 * the backend converter and must not change.
 */
export function mergeUrlFiltersIntoGroup(
  group: ConditionGroup,
  urlFilters: Filter[],
  allFields: Field[],
): ConditionGroup {
  const resolvable = (urlFilters ?? []).filter(
    (f) => !!findFieldByKey(allFields, f.key) || SYSTEM_FILTER_FIELDS.has(f.key),
  );
  if (!resolvable.length) return group;

  const canonicalUrlRows: Filter[] = resolvable.map((f) => ({
    ...f,
    key: canonicalFieldKey(allFields, f.key),
    ...(f.value === "" && (f.operator === "=" || f.operator === "≠")
      ? { operator: f.operator === "=" ? "∅" : "⊙", value: "" }
      : {}),
  }));
  // Same-field matching is bare/dotted-agnostic; built from the KEPT rows
  // only, so a dropped unresolvable param never deletes a saved leaf.
  const urlBareKeys = new Set(resolvable.map((f) => bareFieldName(f.key)));
  const onUrlKey = (leaf: ConditionFilter): boolean =>
    urlBareKeys.has(bareFieldName(leaf.key));

  // An OR group (recursively) made up ONLY of URL-key leaves — the whole
  // group is the thing being replaced.
  const allOnUrlKeys = (g: ConditionGroup): boolean =>
    ((g.filters?.length ?? 0) > 0 || (g.groups?.length ?? 0) > 0) &&
    (g.filters ?? []).every(onUrlKey) &&
    (g.groups ?? []).every(allOnUrlKeys);

  const pruneKeys = (g: ConditionGroup): ConditionGroup | null => {
    if (g.operator !== "AND") return allOnUrlKeys(g) ? null : g;
    const filters = (g.filters ?? []).filter((leaf) => !onUrlKey(leaf));
    const groups = (g.groups ?? [])
      .map(pruneKeys)
      .filter((sub): sub is ConditionGroup => sub !== null);
    if (filters.length === 0 && groups.length === 0) return null;
    return { operator: g.operator, filters, groups };
  };

  const pruned = pruneKeys(group) ?? {
    operator: "AND" as const,
    filters: [],
    groups: [],
  };
  return mergeFlatIntoGroup(pruned, canonicalUrlRows);
}
